import React, { useState } from 'react';
import { BarChart3, ClipboardCheck, Users, Eye, EyeOff } from 'lucide-react';
import { ToastProvider, useToast } from './Toast';

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.content || '';

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isSameOrigin = (url) => {
  try { return new URL(url, window.location.origin).origin === window.location.origin; }
  catch { return false; }
};

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const LinkedInIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0A66C2">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const OAuthButtons = ({ googleSsoEnabled, linkedinSsoEnabled, actionPrefix }) => {
  if (!googleSsoEnabled && !linkedinSsoEnabled) return null;

  const trackClick = (provider) => {
    if (window.ahoy) {
      window.ahoy.track(`succeedo_auth_${actionPrefix}_oauth_click`, { provider });
    }
  };

  const buttonClass =
    'w-full border border-border rounded-full py-3 flex items-center justify-center gap-3 text-[15px] text-foreground hover:bg-accent transition-colors';

  return (
    <div className="space-y-3">
      {googleSsoEnabled && (
        <a href="/auth/google_oauth2" onClick={() => trackClick('google')} className={buttonClass}>
          <GoogleIcon />
          {actionPrefix === 'login' ? 'Continue with Google' : 'Sign up with Google'}
        </a>
      )}
      {linkedinSsoEnabled && (
        <a href="/auth/linkedin" onClick={() => trackClick('linkedin')} className={buttonClass}>
          <LinkedInIcon />
          {actionPrefix === 'login' ? 'Continue with LinkedIn' : 'Sign up with LinkedIn'}
        </a>
      )}
    </div>
  );
};

const OrDivider = ({ text = 'or' }) => (
  <div className="relative my-6">
    <div className="absolute inset-0 flex items-center">
      <div className="w-full border-t border-border" />
    </div>
    <div className="relative flex justify-center">
      <span className="bg-white px-3 font-mono text-[10px] uppercase text-muted-foreground/40">
        {text}
      </span>
    </div>
  </div>
);

// Phase 5: who sent the owner here (an advisor's link or an enterprise
// page), shown above the form. From an enterprise page the owner may also ask
// for an advisor; the box starts unticked and nothing reaches an advisor
// without it.
const SponsorBanner = ({ sponsor, contactOptIn, setContactOptIn }) => (
  <div className="mb-6 rounded-xl border border-border bg-muted/40 p-3 text-[13px]" data-testid="sponsor-banner">
    {sponsor.advisorName ? (
      <p className="m-0 text-foreground">
        Invited by <strong>{sponsor.advisorName}</strong>
        {sponsor.enterprise ? `, ${sponsor.enterprise}` : ''}. They'll be on your team in Succeedo.
      </p>
    ) : (
      <p className="m-0 text-foreground">Free for business owners, offered by {sponsor.enterprise}.</p>
    )}
    {sponsor.askContact && (
      <label className="mt-2 flex items-start gap-2 cursor-pointer text-foreground">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={contactOptIn}
          onChange={() => setContactOptIn(!contactOptIn)}
          data-testid="contact-opt-in"
        />
        <span>
          Have a {sponsor.enterprise} advisor contact me
          <span className="block text-muted-foreground">Optional. You can also ask later from your team page.</span>
        </span>
      </label>
    )}
  </div>
);

const SucceedoLoginForm = ({ googleSsoEnabled, linkedinSsoEnabled, sponsor }) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState(sponsor ? 'signup' : 'login');
  const [contactOptIn, setContactOptIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState('owner');
  const [submitting, setSubmitting] = useState(false);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginErrors, setLoginErrors] = useState({});

  // Signup form state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [signupErrors, setSignupErrors] = useState({});

  const hasOAuth = googleSsoEnabled || linkedinSsoEnabled;

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!loginEmail.trim()) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(loginEmail)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!loginPassword) {
      errors.password = 'Password is required';
    }

    setLoginErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const response = await fetch('/auth/identity/callback', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          authenticity_token: csrfToken(),
          auth_key: loginEmail,
          password: loginPassword,
        }),
      });

      // On success the server redirects to /succeedo/home (or similar).
      // On failure OmniAuth stays at /auth/* or redirects to /login.
      // Any final URL still inside /auth/ or /login means failure.
      const url = response.url || '';
      if (url.includes('/auth/') || url.includes('/login')) {
        toast({
          title: 'Sign in failed',
          description: 'Invalid email or password. Please try again.',
          variant: 'destructive',
        });
      } else {
        window.location.href = isSameOrigin(url) ? url : '/';
      }
    } catch {
      toast({
        title: 'Connection error',
        description: 'Unable to reach the server. Please check your connection and try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!signupName.trim()) {
      errors.name = 'Full name is required';
    }

    if (!signupEmail.trim()) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(signupEmail)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!signupPassword) {
      errors.password = 'Password is required';
    } else if (signupPassword.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (!signupConfirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (signupPassword !== signupConfirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setSignupErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const response = await fetch('/succeedo/register', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({
          name: signupName,
          email: signupEmail,
          password: signupPassword,
          password_confirmation: signupConfirmPassword,
          persona_hint: selectedPersona === 'owner' ? 'seller' : 'buyer',
          contact_opt_in: Boolean(sponsor?.askContact && contactOptIn),
        }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        window.location.href = data.redirect_to || '/succeedo/onboarding';
      } else {
        const errorMsg = (data.errors && data.errors.length > 0)
          ? data.errors.join('. ')
          : 'Unable to create your account. The email may already be in use.';
        toast({
          title: 'Account creation failed',
          description: errorMsg,
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Connection error',
        description: 'Unable to reach the server. Please check your connection and try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (hasError) =>
    `w-full bg-background border rounded-[8px] px-4 py-3 text-foreground focus:outline-none ${
      hasError
        ? 'border-destructive focus:border-destructive'
        : 'border-input focus:border-[hsl(var(--succeedo-orange))]'
    }`;

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <div className="w-full max-w-[1000px] min-h-[600px] mx-auto rounded-[20px] overflow-hidden bg-card border border-border shadow-2xl flex flex-col md:flex-row">
        {/* LEFT PANEL - Brand / Value Prop */}
        <div className="bg-background border-r border-border flex flex-col justify-between p-8 md:p-12 md:w-1/2">
          <div>
            <div className="mb-3">
              <img
                src="https://findbob-production.s3.us-west-2.amazonaws.com/images/succeedo_brand.png"
                alt="Succeedo"
                className="h-7"
              />
            </div>
            <p className="text-[14px] text-muted-foreground">
              Canada&apos;s SMB succession platform.
            </p>
          </div>

          <div className="mt-12 hidden md:block">
            <h2 className="font-serif text-[38px] font-light text-foreground tracking-[-0.03em] leading-[1.1]">
              Your free toolkit for a successful exit.
            </h2>
            <p className="text-[16px] text-muted-foreground mt-4">
              Free tools, personalized plan, expert introductions — everything
              you need to transition your business on your terms.
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3">
                <BarChart3
                  className="w-5 h-5 text-[hsl(var(--succeedo-orange))] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <div>
                  <div className="text-[14px] text-foreground font-medium">
                    Valuation Studio
                  </div>
                  <div className="text-[13px] text-muted-foreground">
                    Know what your business is worth
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ClipboardCheck
                  className="w-5 h-5 text-[hsl(var(--succeedo-orange))] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <div>
                  <div className="text-[14px] text-foreground font-medium">
                    Personalized 90-Day Plan
                  </div>
                  <div className="text-[13px] text-muted-foreground">
                    Built from your Common Sense Score&trade;
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users
                  className="w-5 h-5 text-[hsl(var(--succeedo-orange))] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <div>
                  <div className="text-[14px] text-foreground font-medium">
                    Expert Partner Introductions
                  </div>
                  <div className="text-[13px] text-muted-foreground">
                    CBVs, brokers, lenders — when you&apos;re ready
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden md:block">
            <p className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide">
              FREE FOR BUSINESS OWNERS &middot; PARTNERS PAY PER INTRODUCTION
            </p>
          </div>
        </div>

        {/* RIGHT PANEL - Form */}
        <div className="bg-white flex flex-col justify-center p-8 md:p-12 md:w-1/2">
          {sponsor && (
            <SponsorBanner sponsor={sponsor} contactOptIn={contactOptIn} setContactOptIn={setContactOptIn} />
          )}
          {/* Tab Row */}
          <div className="flex gap-8 border-b border-border">
            <button
              type="button"
              onClick={() => setActiveTab('login')}
              className={`font-mono text-[11px] uppercase pb-2 border-b-2 transition-colors ${
                activeTab === 'login'
                  ? 'text-foreground border-[hsl(var(--succeedo-orange))]'
                  : 'text-muted-foreground/50 border-transparent'
              }`}
            >
              LOG IN
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('signup')}
              className={`font-mono text-[11px] uppercase pb-2 border-b-2 transition-colors ${
                activeTab === 'signup'
                  ? 'text-foreground border-[hsl(var(--succeedo-orange))]'
                  : 'text-muted-foreground/50 border-transparent'
              }`}
            >
              CREATE ACCOUNT
            </button>
          </div>

          {/* LOG IN STATE */}
          {activeTab === 'login' && (
            <div>
              <h2 className="text-[22px] font-semibold text-foreground mt-6">
                Welcome back.
              </h2>

              <form onSubmit={handleLoginSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="block text-[13px] text-muted-foreground mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="you@yourbusiness.com"
                    value={loginEmail}
                    onChange={(e) => {
                      setLoginEmail(e.target.value);
                      if (loginErrors.email) {
                        setLoginErrors({ ...loginErrors, email: undefined });
                      }
                    }}
                    className={inputClass(loginErrors.email)}
                  />
                  {loginErrors.email && (
                    <p className="text-[12px] text-destructive mt-1">{loginErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[13px] text-muted-foreground mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => {
                        setLoginPassword(e.target.value);
                        if (loginErrors.password) {
                          setLoginErrors({ ...loginErrors, password: undefined });
                        }
                      }}
                      className={`${inputClass(loginErrors.password)} pr-12`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-muted-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {loginErrors.password ? (
                    <p className="text-[12px] text-destructive mt-1">{loginErrors.password}</p>
                  ) : (
                    <a
                      href="/password_resets/new"
                      className="block text-right text-[13px] text-muted-foreground/50 hover:text-[hsl(var(--succeedo-orange))] mt-1 transition-colors"
                    >
                      Forgot password?
                    </a>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-foreground text-background rounded-full py-3 text-[15px] font-medium mt-6 hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {submitting ? 'Signing in\u2026' : 'Sign In'}
                </button>
              </form>

              {/* Divider */}
              <OrDivider />

              {/* OAuth Buttons */}
              <OAuthButtons
                googleSsoEnabled={googleSsoEnabled}
                linkedinSsoEnabled={linkedinSsoEnabled}
                actionPrefix="login"
              />

              {hasOAuth && <div className="mt-3" />}

              <button
                type="button"
                onClick={() => setActiveTab('signup')}
                className="w-full border border-border text-foreground rounded-full py-3 text-[15px] hover:bg-accent transition-colors"
              >
                Create a free account
              </button>
              <p className="font-mono text-[10px] uppercase text-muted-foreground/40 text-center mt-3">
                FREE &middot; NO CREDIT CARD REQUIRED
              </p>
            </div>
          )}

          {/* CREATE ACCOUNT STATE */}
          {activeTab === 'signup' && (
            <div>
              <h2 className="text-[22px] font-semibold text-foreground mt-6">
                Start for free.
              </h2>

              <form onSubmit={handleSignupSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="block text-[13px] text-muted-foreground mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    placeholder="Jane Smith"
                    value={signupName}
                    onChange={(e) => {
                      setSignupName(e.target.value);
                      if (signupErrors.name) {
                        setSignupErrors({ ...signupErrors, name: undefined });
                      }
                    }}
                    className={inputClass(signupErrors.name)}
                  />
                  {signupErrors.name && (
                    <p className="text-[12px] text-destructive mt-1">{signupErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[13px] text-muted-foreground mb-2">
                    Business Email
                  </label>
                  <input
                    type="email"
                    placeholder="jane@acmeplumbing.ca"
                    value={signupEmail}
                    onChange={(e) => {
                      setSignupEmail(e.target.value);
                      if (signupErrors.email) {
                        setSignupErrors({ ...signupErrors, email: undefined });
                      }
                    }}
                    className={inputClass(signupErrors.email)}
                  />
                  {signupErrors.email && (
                    <p className="text-[12px] text-destructive mt-1">{signupErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[13px] text-muted-foreground mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={signupPassword}
                      onChange={(e) => {
                        setSignupPassword(e.target.value);
                        if (signupErrors.password) {
                          setSignupErrors({ ...signupErrors, password: undefined });
                        }
                      }}
                      className={`${inputClass(signupErrors.password)} pr-12`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-muted-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {signupErrors.password && (
                    <p className="text-[12px] text-destructive mt-1">{signupErrors.password}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[13px] text-muted-foreground mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={signupConfirmPassword}
                      onChange={(e) => {
                        setSignupConfirmPassword(e.target.value);
                        if (signupErrors.confirmPassword) {
                          setSignupErrors({ ...signupErrors, confirmPassword: undefined });
                        }
                      }}
                      className={`${inputClass(signupErrors.confirmPassword)} pr-12`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-muted-foreground"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {signupErrors.confirmPassword && (
                    <p className="text-[12px] text-destructive mt-1">{signupErrors.confirmPassword}</p>
                  )}
                </div>

                {/* Persona Selector */}
                <div className="mt-4">
                  <label className="block font-mono text-[11px] uppercase text-muted-foreground mb-3">
                    I AM A
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedPersona('owner')}
                      className={`flex-1 rounded-full px-4 py-2 text-[13px] transition-colors ${
                        selectedPersona === 'owner'
                          ? 'bg-foreground text-background font-medium'
                          : 'border border-border text-muted-foreground'
                      }`}
                    >
                      Business Owner
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedPersona('buyer')}
                      className={`flex-1 rounded-full px-4 py-2 text-[13px] transition-colors ${
                        selectedPersona === 'buyer'
                          ? 'bg-foreground text-background font-medium'
                          : 'border border-border text-muted-foreground'
                      }`}
                    >
                      Buyer
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[hsl(var(--succeedo-orange))] text-white rounded-full py-3 text-[15px] font-medium mt-6 hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {submitting ? 'Creating account\u2026' : 'Create My Free Account \u2192'}
                </button>

                <p className="font-mono text-[10px] uppercase text-muted-foreground/40 text-center mt-4">
                  BY SIGNING UP YOU AGREE TO OUR TERMS OF SERVICE AND PRIVACY
                  POLICY
                </p>
              </form>

              {/* OAuth signup buttons */}
              {hasOAuth && <OrDivider text="or sign up with" />}
              <OAuthButtons
                googleSsoEnabled={googleSsoEnabled}
                linkedinSsoEnabled={linkedinSsoEnabled}
                actionPrefix="signup"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SucceedoLogin = ({ googleSsoEnabled, linkedinSsoEnabled, sponsor = null }) => (
  <ToastProvider>
    <SucceedoLoginForm
      googleSsoEnabled={googleSsoEnabled}
      linkedinSsoEnabled={linkedinSsoEnabled}
      sponsor={sponsor}
    />
  </ToastProvider>
);

export default SucceedoLogin;
